import { isEmpty } from 'lodash';
import { dataSchemaToTSDeclaration } from '@tencent/data-schema-core/lib/convertor/typescript';
import { getValidPascalCase } from '@tencent/data-schema-core/lib/convertor/typeOptions';
import { requestCGI } from './requestCGI';
import { getRuntimeConfig } from './collectionAPI';
import { DataSourceBase } from './DataSourceBase';
import { DATA_SOURCE_TYPE_PREFIX } from './utils/const';
import { checkIsDBMethodName, checkIsMultiHTTPCollection } from './utils/tools';
import { DBSource } from './DBSource';
import { DBDatasourceMethods } from './types/DBSource';
import commonTSDeclaration from '!!raw-loader!./types/DBSource.d.ts';
const renameMethodByRealMethod = {
    [DBDatasourceMethods.get]: 'getById',
    [DBDatasourceMethods.list]: 'query',
    [DBDatasourceMethods.create]: 'create',
    [DBDatasourceMethods.update]: 'update',
    [DBDatasourceMethods.delete]: 'delete',
    [DBDatasourceMethods.count]: 'count',
};
export class HTTPSource extends DataSourceBase {
    async getTSDeclaration({ typePrefix = DATA_SOURCE_TYPE_PREFIX, source, withCommon = true, }) {
        const collection = source || await getRuntimeConfig({
            projectId: this.projectId,
            collectionId: this.collectionId,
        });
        const { cgiInfo, entity = {} } = collection;
        // HTTP多接口数据源可能只实现了部分方法，需要过滤一下
        const isMultiHTTP = checkIsMultiHTTPCollection(collection);
        const implementedMethods = Object.keys(collection.cgiInfo).filter(key => checkIsDBMethodName(key));
        const entityTS = dataSchemaToTSDeclaration({
            ...entity,
            id: entity?.id || this.collectionId,
            disableCache: true,
        }, { noDsTitle: true });
        const tsMap = {};
        Object.entries(cgiInfo)
            .filter(([key]) => !isMultiHTTP || !implementedMethods.includes(key))
            .forEach(([methodName, info]) => {
            const processSchema = (type) => {
                const fixedSchema = info[`fixed${type.charAt(0).toUpperCase() + type.slice(1)}Schema`];
                const fallbackSchema = info[`${type}Schema`] || {};
                const schema = isEmpty(fixedSchema) ? fallbackSchema : fixedSchema;
                // 给一个id，增强生成出来的类型可读性,最终会转成PascalCase
                schema.id = `${this.collectionId}-${methodName}-${type}`;
                return dataSchemaToTSDeclaration({ ...schema, disableCache: true }, { noDsTitle: true });
            };
            tsMap[methodName] = {
                query: processSchema('query'),
                body: processSchema('body'),
                response: processSchema('response'),
            };
            // 处理特殊响应类型
            const { responseType } = info;
            if (responseType === 'blob') {
                tsMap[methodName].response = {
                    exportedTypeId: 'Blob',
                    getFullCode: () => '',
                };
            }
            else if (responseType === 'base64') {
                tsMap[methodName].response = {
                    exportedTypeId: 'Base64String',
                    getFullCode: () => 'type Base64String = `data:${string};base64,${string}`;',
                };
            }
            else if (responseType === 'arraybuffer') {
                tsMap[methodName].response = {
                    exportedTypeId: 'ArrayBuffer',
                    getFullCode: () => '',
                };
            }
        });
        const ts = `type ${getValidPascalCase(`${typePrefix}-${this.collectionId}`)} = {
${Object.entries(tsMap).reduce((acc, [key, value]) => `${acc} ${key}: (query: ${value.query.exportedTypeId}, body: ${value.body.exportedTypeId}) => Promise<${value.response.exportedTypeId}>;\n`, '')}} ${implementedMethods.length ? `& Pick<DBObject<${entityTS.exportedTypeId}>, ${implementedMethods.map(m => `"${renameMethodByRealMethod[m]}"`).join('|')}>\n\n${entityTS.getFullCode()}\n${withCommon ? commonTSDeclaration : ''}` : ''}

${Object.values(tsMap).reduce((acc, value) => `${acc}${value.query.getFullCode()}\n${value.body.getFullCode()}\n${value.response.getFullCode()}\n`, '')}`;
        return ts;
    }
    async getExtraComments() {
        return '';
    }
    async getMethods({ source }) {
        const collection = source || await this.getCollectionInfo();
        const { cgiInfo } = collection;
        const isMultiHTTPCollection = checkIsMultiHTTPCollection(collection);
        const methods = {
            ...(isMultiHTTPCollection
                ? await new DBSource({
                    collectionId: this.collectionId,
                    projectId: this.projectId,
                }).getMethods()
                : {}),
        };
        Object.keys(cgiInfo)
            .filter(methodName => !isMultiHTTPCollection || !checkIsDBMethodName(methodName))
            .forEach((methodName) => {
            methods[methodName] = async (query, body) => {
                const response = await requestCGI(this.collectionId, methodName, { query, body }, {});
                return response.data;
            };
        });
        return methods;
    }
}
